import { TIME_ZONE, toISODate } from "./day";

/**
 * Placeholder for a value the API did not supply. Shared so every table cell,
 * detail row and badge falls back to the same glyph — an em dash, not a
 * hyphen — instead of each feature declaring its own near-identical constant.
 */
export const EMPTY_VALUE = "—";

interface PersonName {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Returns up to two uppercase initials from a structured name.
 * The subName (tussenvoegsel) is intentionally excluded — pass only
 * firstName and lastName.
 */
export function initials({ firstName, lastName }: PersonName): string {
  return [firstName, lastName]
    .filter((part): part is string => Boolean(part))
    .map((part) => part[0].toUpperCase())
    .join("");
}

/**
 * Shortens a full name to "J. Surname" so it reliably fits a fixed-width UI
 * slot (e.g. the sidebar identity footer) without truncating mid-word.
 *
 * Abbreviates the first name only, following the Dutch convention (used on
 * official correspondence, business cards, and NEN 5825 alphabetization)
 * rather than the Anglo "First L." pattern: the latter drops the tussenvoegsel
 * entirely (e.g. "Jan van der Berg" -> "Jan B."), while this keeps the full
 * surname, tussenvoegsel included ("J. van der Berg"). It also targets the
 * part of the name with unpredictable length, since first names vary far more
 * ("Christiaan" vs. "Jan") than a one-letter initial ever does.
 *
 * Single-word names (no surname to keep) are returned as-is.
 */
export function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return fullName;
  const [firstName, ...surname] = parts;
  return `${firstName[0].toUpperCase()}. ${surname.join(" ")}`;
}

/**
 * Renders a nullable boolean as Dutch "Ja"/"Nee".
 *
 * A `null`/`undefined` answer returns `undefined` rather than "Nee", so a
 * caller can drop the row entirely: on a form-answer field "unknown" and "no"
 * are genuinely different facts, and rendering a missing answer as "Nee"
 * would assert something the user never said.
 */
export function yesNo(value: boolean | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  return value ? "Ja" : "Nee";
}

// Pinned to the affiliate's timezone rather than the machine's.

const dateFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: TIME_ZONE,
});

const timeFormatter = new Intl.DateTimeFormat("nl-NL", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: TIME_ZONE,
});

/**
 * Formats an ISO date string as a short Dutch date (e.g. "05 jul 2026").
 * Returns null for an invalid input so callers can fall back.
 */
export function formatDate(value: string): string | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateFormatter.format(date);
}

/**
 * {@link formatDate} for read-only display, where a missing or unparseable
 * date is simply "nothing to show": collapses both cases to
 * {@link EMPTY_VALUE} so callers can drop the value straight into JSX.
 * Prefer {@link formatDate} when absent and invalid need different handling.
 */
export function formatDateOrEmpty(value: string | null | undefined): string {
  if (!value) return EMPTY_VALUE;
  return formatDate(value) ?? EMPTY_VALUE;
}

/**
 * Formats an ISO date string as a short Dutch date with time (e.g. "05 jul 2026 14:05").
 * Returns null for an invalid input so callers can fall back.
 */
export function formatDateTime(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${dateFormatter.format(date)} ${timeFormatter.format(date)}`;
}

const shortDateNoYearFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  timeZone: TIME_ZONE,
});

/**
 * Formats an ISO date string as a compact Dutch date with time, without the
 * year (e.g. "12 sep 14:30"). For slots too narrow for {@link formatDateTime}'s
 * full form — e.g. a dashboard card summarising recent items, where the year
 * is implied. Returns null for an invalid input so callers can fall back.
 */
export function formatShortDateTime(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${shortDateNoYearFormatter.format(date)} ${timeFormatter.format(date)}`;
}

/**
 * Formats an ISO date string as a Dutch time of day (e.g. "14:05").
 * Returns null for an invalid input so callers can fall back.
 */
export function formatTime(value: string): string | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : timeFormatter.format(date);
}

/**
 * Formats a start/end pair as a Dutch time range ("09:00 – 12:00"), or just the
 * start when there is no end -- an appointment often has only a starting time.
 * Returns null when the start is missing or unparseable, so a read-only field
 * can be dropped rather than shown half-empty.
 */
export function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  const startTime = start ? formatTime(start) : null;
  if (!startTime) return null;

  const endTime = end ? formatTime(end) : null;

  return endTime ? `${startTime} – ${endTime}` : startTime;
}

/**
 * {@link formatDateTime} for read-only display, collapsing a missing or
 * unparseable moment to {@link EMPTY_VALUE}. The time-of-day counterpart of
 * {@link formatDateOrEmpty}.
 */
export function formatDateTimeOrEmpty(
  value: string | null | undefined,
): string {
  if (!value) return EMPTY_VALUE;
  return formatDateTime(value) ?? EMPTY_VALUE;
}

/**
 * Trims an ISO date-time down to the `YYYY-MM-DD` a date control expects
 * (`DatePicker`, `<input type="date">`), or `""` when there is no value.
 *
 * Deliberately a string slice rather than a `Date` round-trip: the backend
 * already sends the calendar day the user picked, and parsing it into a
 * `Date` only to re-serialize it would shift the day across a timezone.
 */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) return "";
  return value.slice(0, 10);
}

/** Today as `YYYY-MM-DD`, in local time so it matches what the user sees. */
export function todayAsDateInput(): string {
  return toISODate(new Date());
}

const numberFormatter = new Intl.NumberFormat("nl-NL");

/** Formats a number using Dutch thousand separators (e.g. "45.000"). */
export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

const currencyFormatter = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
});

/**
 * Formats a price held in minor units (cents) as a Dutch euro string
 * (e.g. 105000 → "€ 1.050,00"), or "—" when there is no price.
 *
 * The API stores money in cents to avoid floating-point drift, so every
 * amount crosses this boundary exactly once on the way to the screen.
 * Use {@link centsToEuroInput} instead when seeding an editable field.
 */
export function formatCentsAsEuros(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return currencyFormatter.format(cents / 100);
}

/**
 * Formats an amount already expressed in euros (e.g. 1050.5 → "€ 1.050,50").
 *
 * Only for figures that reach us in major units: the parsed Audatex document is
 * a transcription of a printed calculation, so its amounts are euros, not the
 * cents the rest of the API deals in. Prefer {@link formatCentsAsEuros}.
 */
export function formatEuros(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return currencyFormatter.format(value);
}

/**
 * Converts cents to the plain euro string a text input expects
 * (e.g. 4500000 → "45000", 1050 → "10.50"). Trailing ",00" is dropped so a
 * whole-euro price does not read as if the user typed decimals.
 *
 * Deliberately unformatted and empty-on-absent, unlike
 * {@link formatCentsAsEuros}: an input's value has to round-trip back through
 * {@link euroInputToCents}, and thousand separators or an em-dash would not.
 */
export function centsToEuroInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  return (cents / 100).toFixed(2).replace(/\.00$/, "");
}

/**
 * Parses a euro amount typed by the user back into cents, returning null for
 * an empty field. Accepts the Dutch comma decimal separator as well as a dot,
 * because a nl-NL keyboard produces "10,50" while the numeric keypad produces
 * "10.50".
 */
export function euroInputToCents(euros: string): number | null {
  const trimmed = euros.trim().replace(",", ".");
  if (trimmed === "") return null;
  return Math.round(Number.parseFloat(trimmed) * 100);
}

const inboxTimeFormatter = new Intl.DateTimeFormat("nl-NL", {
  hour: "2-digit",
  minute: "2-digit",
});

const inboxWeekdayTimeFormatter = new Intl.DateTimeFormat("nl-NL", {
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const inboxDayMonthFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric",
  month: "short",
});

const inboxDayMonthYearFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** Calendar-day difference between two dates, ignoring the time of day. */
function calendarDaysBetween(from: Date, to: Date): number {
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round(
    (toDay.getTime() - fromDay.getTime()) / (24 * 60 * 60 * 1000),
  );
}

/**
 * Formats a timestamp for an inbox row, where horizontal space is scarce and
 * recency matters more than precision. Progressively drops detail as the
 * message ages:
 *
 * - today            → `14:05`
 * - within a week    → `di 14:05`
 * - this year        → `5 jul`
 * - older            → `5 jul 2026`
 *
 * Mirrors the design-lab prototype's `formatInboxTimestamp`, but anchored to
 * the real clock instead of the prototype's fixed `REFERENCE_NOW`. `now` is
 * injectable so tests are not time-dependent.
 *
 * Returns null for an invalid input so callers can fall back, matching
 * {@link formatDate} and {@link formatDateTime}.
 */
export function formatInboxTimestamp(
  value: string,
  now: Date = new Date(),
): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const dayDelta = calendarDaysBetween(date, now);

  // Future timestamps (clock skew between server and client) read as "now"
  // rather than showing a bare date the user cannot place.
  if (dayDelta <= 0) return inboxTimeFormatter.format(date);
  if (dayDelta < 7) return inboxWeekdayTimeFormatter.format(date);
  if (date.getFullYear() === now.getFullYear()) {
    return inboxDayMonthFormatter.format(date);
  }
  return inboxDayMonthYearFormatter.format(date);
}

const longDateFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const shortDateFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const monthYearFormatter = new Intl.DateTimeFormat("nl-NL", {
  month: "long",
  year: "numeric",
});

const weekdayFormatter = new Intl.DateTimeFormat("nl-NL", { weekday: "short" });

/**
 * Formats a `Date` as a full Dutch date (e.g. "15 juni 2024"). Unlike
 * {@link formatDate}, this takes an already-validated `Date` rather than an
 * ISO string, and has no null-fallback: callers (e.g. `DatePicker`) hold a
 * `Date` they have already confirmed is valid.
 */
export function formatLongDate(date: Date): string {
  return longDateFormatter.format(date);
}

/**
 * {@link formatLongDate} with an abbreviated month (e.g. "15 jun 2024"), for
 * slots too narrow for the full month name — the date fields inside the
 * period picker's popover. Same already-validated-`Date` contract.
 */
export function formatShortDate(date: Date): string {
  return shortDateFormatter.format(date);
}

/** Formats a `Date` as a Dutch month and year (e.g. "juni 2024"). */
export function formatMonthYear(date: Date): string {
  return monthYearFormatter.format(date);
}

/**
 * Dutch, Monday-first short weekday labels (`["ma", "di", ..., "zo"]`).
 * Derived from a known Monday (2024-01-01) rather than hardcoded, so the
 * labels always match the locale's actual weekday abbreviations. Noon is
 * used to keep the anchor clear of any DST transition.
 */
export const mondayFirstWeekdayLabels: readonly string[] = Array.from(
  { length: 7 },
  (_, index) => weekdayFormatter.format(new Date(2024, 0, 1 + index, 12)),
);
